import { Tooltip } from 'antd';
import cn from 'classnames';
import Lottie from 'lottie-react-web';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ReactSVG } from 'react-svg';
import {
    levelPositions,
    levelPositionsForFiveMissions,
    levelPositionsForFiveMissionSmallDsk,
    levelPositionsForSmallDsk
} from '../../utils/constants';
import BonusCard from '../BonusCard';
import animation from './animation/animation.json';
import './styles.scss';

interface GameMapProps {
    data: any[];
    onClose?: () => void;
}

const GameMap: FC<GameMapProps> = ({ data, onClose }) => {
    const [selectedMission, setSelectedMission] = useState<Record<string, string> | null>(null);
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const [tooltipPosition, setTooltipPosition] = useState<Record<string, number>>({});
    const draggableRef = useRef<any>(null);
    const [x, setX] = useState<number>(24);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const bounds = { left: -620, right: 80 };
    const isSmallDesktop = window.innerHeight < 600;

    const indexOfInProgress = useMemo(
        () => data?.findIndex((mission) => mission?.status === 'inProgress' || mission?.status === 'optInRequired'),
        [data]
    );

    const levelPositionsToUse = useMemo(() => {
        if (data?.length <= 5) {
            return isSmallDesktop ? levelPositionsForFiveMissionSmallDsk : levelPositionsForFiveMissions;
        } else {
            return isSmallDesktop ? levelPositionsForSmallDsk : levelPositions;
        }
    }, [data?.length, isSmallDesktop]);

    const handleMarkClick = (mission: Record<string, string>, i: number) => {
        setSelectedMission(mission);
        setTooltipPosition(levelPositionsToUse[i]);
        setX(-levelPositionsToUse[i].left / 2.1);
        setTooltipOpen(false);
        setTimeout(() => setTooltipOpen(true), 0);
    };

    const memoizedBonusCard = useMemo(() => {
        return selectedMission ? <BonusCard data={selectedMission} onClose={onClose} /> : null;
    }, [selectedMission, onClose]);

    const moveBox = (direction: 'left' | 'right') => {
        setIsDragging(false);
        setX((prevX) => {
            return direction === 'left' ? Math.max(prevX - 125, bounds.left) : Math.min(prevX + 125, bounds.right);
        });
    };

    useEffect(() => {
        if (indexOfInProgress < 0) return;
        setX(-levelPositionsToUse[indexOfInProgress].left / 2.1);
    }, [indexOfInProgress]);

    const getStatusIcon = useMemo(
        () => (status: string) => {
            switch (status) {
                case 'announced':
                    return <img className="icon" src="/static/images/locked.png" alt="locked" />;
                case 'completed':
                    return <img className="icon passed_icon" src="/static/images/passed.png" alt="passed" />;
                case 'inProgress':
                case 'optInRequired':
                    return (
                        <div className="progress_mission">
                            <Lottie
                                options={{
                                    animationData: animation,
                                }}
                                width={150}
                                height={150}
                            />
                        </div>
                    );
                default:
                    return null;
            }
        },
        []
    );

    return (
        <>
            {data?.length > 5 && (
                <div className="navigations_of_map">
                    <button onClick={() => moveBox('right')}>
                        <ReactSVG src="/static/images/chevron_left.svg" />
                    </button>
                    <button onClick={() => moveBox('left')}>
                        <ReactSVG src="/static/images/chevron_right.svg" />
                    </button>
                </div>
            )}
            <Draggable
                nodeRef={draggableRef}
                position={{ x: data?.length <= 5 ? 0 : x, y: 0 }} // Start position
                onDrag={(_, data) => {
                    requestAnimationFrame(() => setX(data.x));
                }}
                onStart={() => setIsDragging(true)}
                onStop={(_, data) => {
                    setIsDragging(false);
                    setX(data.x);
                }}
                axis="x"
                disabled={data?.length <= 5}
                bounds={bounds}
            >
                <div
                    style={{
                        transition: isDragging ? 'none' : 'transform 0.3s ease',
                        transform: `translateX(${x}px)`,
                    }}
                    ref={draggableRef}
                    className={cn('map-container', { containerFor_maxFive_missions: data?.length <= 5 })}
                >
                    <div className="map">
                        <Tooltip
                            arrow={false}
                            trigger={'click'}
                            open={tooltipOpen}
                            onOpenChange={setTooltipOpen}
                            title={memoizedBonusCard}
                            getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement as HTMLElement}
                            overlayStyle={{
                                top: isSmallDesktop ? 0 : tooltipPosition['top'] / 4.6,
                                left: tooltipPosition['left'] / 1.5,
                            }}
                        >
                            {data.map((_item, i) => (
                                <div
                                    key={i}
                                    className="level"
                                    onClick={() => handleMarkClick(data[i], i)}
                                    style={{ top: `${levelPositionsToUse[i].top}px`, left: `${levelPositionsToUse[i].left}px` }}
                                >
                                    <div
                                        className={cn('marker', {
                                            [data[i]?.status === 'announced' ? 'locked' : data[i]?.status]: !!data[i]?.status,
                                        })}
                                    >
                                        {getStatusIcon(data[i]?.status)}
                                        <img src={data[i]?.img_url} width={72} alt={data[i]?.img_url} />
                                    </div>
                                </div>
                            ))}
                        </Tooltip>
                    </div>
                </div>
            </Draggable>
        </>
    );
};

export default GameMap;
